import React, {useState, useMemo, useCallback, useEffect} from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  Box,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
  Build as BuildIcon,
  AddCircle as AddIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  HelpOutline as HelpOutlineIcon,
  FolderOutlined as FolderIcon,
  Close as CloseIcon,
  InsertDriveFileOutlined as FileIcon,
  Code as CodeIcon,
  AccountTree as DependencyIcon,
  CheckCircleOutline as StepIcon,
  DataObject as XmlIcon,
} from '@mui/icons-material';
import {Button} from '@mui/material';
import {Notification} from '@signanthealth/modular-ui';
import {apiService} from '../services/api';
import {useWizardNavigation} from '../contexts/WizardNavigationContext';

// ============================================
// INTERFACES
// ============================================

interface ActionParameters {
  kb_entry_name?: string;
  kb_platform?: string;
  kb_entry_type?: string;
  kb_relevant_sections?: string[];
  kb_dependencies?: string[];
  kb_xml_available?: boolean;
  [key: string]: unknown;
}

interface ProposedChange {
  change_type: string;
  description: string;
  target_file?: string;
  xml_fragment?: string;
}

interface TranslationChange {
  change_type: string;
  target_file: string;
  text_id?: string;
  description: string;
  xml_fragment?: string;
}

interface XmlChanges {
  codelist_name?: string;
  codelist_items?: Array<{
    coded_value: string;
    text_id: string;
    text_value: string;
  }>;
  codelist_xml?: string;
  codelist_item_count?: number;
  next_coded_value?: string;
  codelist_file?: string;
  translation_files?: string[];
  proposed_changes?: ProposedChange[];
  translation_changes?: TranslationChange[];
  [key: string]: any;
}

interface PlanAction {
  // New format fields
  id?: string;
  type?: string;
  target?: string;
  target_element?: string;
  target_section?: string;
  description?: string;
  parameters?: ActionParameters;
  xml_changes?: XmlChanges;
  validation_rules?: unknown[];
  target_files?: string[];
  concrete_instructions?: string;
  // Legacy + shared fields
  action: string;
  target_element_name?: string;
  target_element_type?: string;
  target_file?: string;
  reason?: string;
  instructions?: string;
  executor?: string;
  requirement_id?: string;
  also_fulfills?: string[];
  confidence?: 'high' | 'medium' | 'low';
  confidence_reason?: string;
  status?: string;
  message?: string;
  text?: string;
  // Action-specific fields for element identification
  name?: string; // download_gl
  varId?: string; // add_variable
  condition_id?: string; // add_condition
  text_id?: string; // add_text
  action_id?: string; // add_action
  OID?: string; // add_data_item or add_codelist
  codelist_oid?: string; // add_codelist
  code_list_oid?: string; // add_codelist
  form_id?: string; // create_custom_form (snake_case)
  formId?: string; // create_custom_form (camelCase variant)
  form_name?: string; // create_custom_form
  page_id?: string; // add_page
  event?: string; // add_on_event
}

interface SurgicalPlan {
  actions: PlanAction[];
  execution_order?: string[];
  system_rules?: any[];
  metadata?: {
    source?: string;
    feature_count?: number;
    dependency_count?: number;
    rule_count?: number;
    builder_prompts?: string[];
  };
}

interface PlanNote {
  requirement_id: string;
  text: string;
  confidence?: string;
}

interface PlanUnresolved {
  requirement_id: string;
  reason: string;
  status?: string;
}

interface ParsedReasonSegment {
  text: string;
  isQuestion: boolean;
}

interface PlanActionsTableProps {
  plan: {
    plan_id?: string;
    reasoning?: string;
    actions: PlanAction[];
    /** Structured notes or plain strings (KB / v2 API) */
    notes?: PlanNote[] | string[];
    /** Structured unresolved items or plain strings (KB / v2 API) */
    unresolved?: PlanUnresolved[] | string[];
    trial_id?: string;
    session_id?: string;
    platform?: 'app' | 'web';
    generated_at?: string;
    kb_confidence?: number;
    feature_count?: number;
    dependency_count?: number;
    surgical_plan?: SurgicalPlan;
  };
  cached?: boolean;
  generatedAt?: string;
  onPlanChanged?: () => void;
  onEditRequirementRequested?: (requirementId: string) => void;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const isNewFormat = (action: PlanAction): boolean => {
  return !!(action.id && action.description && (action.parameters || action.target_files));
};

const getElementType = (action: PlanAction): string => {
  if (action.target_element_type) return action.target_element_type.toUpperCase();
  switch (action.action) {
    case 'unresolved':
    case 'note':
      return '-';
    case 'download_gl':
      return 'GL';
    case 'add_variable':
      return 'VARIABLE';
    case 'add_condition':
      return 'CONDITION';
    case 'add_text':
      return 'TEXT';
    case 'add_action':
      return 'ACTION';
    case 'create_custom_form':
      return 'FORM';
    default:
      return 'ELEMENT';
  }
};

const getElementId = (action: PlanAction): string => {
  if (action.target) return action.target;
  if (action.target_element) return action.target_element;
  switch (action.action) {
    case 'unresolved':
    case 'note':
      return '-';
    case 'download_gl':
      return action.name || '-';
    case 'add_variable':
      return action.varId || '-';
    case 'add_condition':
      return action.condition_id || '-';
    case 'add_text':
      return action.text_id || '-';
    case 'add_action':
      return action.action_id || '-';
    case 'add_data_item':
      return action.OID || '-';
    case 'add_page':
      return action.page_id || '-';
    case 'add_codelist':
      return action.codelist_oid || action.code_list_oid || action.OID || '-';
    case 'add_on_event':
      return action.event || action.event || '-';
    case 'create_custom_form':
      return action.form_id || action.formId || action.form_name || '-';
    default:
      return '-';
  }
};


const getAllTargetFiles = (action: PlanAction): string[] => {
  const files: string[] = [];
  if (action.target_files && action.target_files.length > 0) {
    files.push(...action.target_files);
  } else if (action.target_file) {
    files.push(action.target_file);
  }
  if (action.xml_changes?.translation_files) {
    for (const tf of action.xml_changes.translation_files) {
      if (!files.includes(tf)) files.push(tf);
    }
  }
  return files.length > 0 ? files : ['protocol'];
};

const getFileDisplay = (filename: string): string => {
  if (filename.includes('protocol') && !filename.includes('.xml')) return 'protocol';
  return filename;
};

const formatActionType = (action: PlanAction): string => {
  if (action.type) {
    return action.type.charAt(0).toUpperCase() + action.type.slice(1);
  }
  return action.action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getDescriptionTitle = (action: PlanAction): string => {
  if (action.description) {
    const firstLine = action.description.split('\n')[0].trim();
    return firstLine;
  }
  if (action.parameters?.kb_entry_name) return action.parameters.kb_entry_name;
  if (action.instructions) return action.instructions.split('\n')[0].trim();
  return action.reason || formatActionType(action);
};

const getDescriptionSteps = (action: PlanAction): string[] => {
  if (!action.description) return [];
  const lines = action.description.split('\n');
  return lines.slice(1).map(l => l.trim()).filter(l => l.length > 0);
};

const getConcreteSteps = (action: PlanAction): string[] => {
  if (action.concrete_instructions) {
    return action.concrete_instructions.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  }
  if (action.instructions && action.instructions.includes('--- Concrete Steps ---')) {
    const parts = action.instructions.split('--- Concrete Steps ---');
    if (parts[1]) {
      return parts[1].split('\n').map(l => l.trim()).filter(l => l.length > 0);
    }
  }
  return [];
};

const getDependencies = (action: PlanAction): string[] => {
  const deps = action.parameters?.kb_dependencies;
  return Array.isArray(deps) ? deps : [];
};

/** Keys used for structured xml_changes objects; any other string key is treated as filename → snippet. */
const XML_CHANGE_STRUCTURE_KEYS = new Set([
  'codelist_name',
  'codelist_items',
  'codelist_xml',
  'codelist_item_count',
  'next_coded_value',
  'codelist_file',
  'translation_files',
  'proposed_changes',
  'translation_changes',
]);

function getXmlChangesByFile(xc: XmlChanges | undefined): Array<{file: string; content: string}> {
  if (!xc || typeof xc !== 'object') return [];
  const out: Array<{file: string; content: string}> = [];
  for (const [k, v] of Object.entries(xc)) {
    if (XML_CHANGE_STRUCTURE_KEYS.has(k)) continue;
    if (typeof v === 'string' && v.trim().length > 0) {
      out.push({file: k, content: v});
    }
  }
  return out;
}

function normalizePlanNotes(raw: PlanNote[] | string[] | undefined): PlanNote[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((item, idx) => {
    if (typeof item === 'string') {
      return {requirement_id: `Note ${idx + 1}`, text: item};
    }
    const o = item as PlanNote;
    return {
      requirement_id: o.requirement_id?.trim() ? o.requirement_id : `Note ${idx + 1}`,
      text: o.text,
      confidence: o.confidence,
    };
  });
}

function normalizePlanUnresolved(raw: PlanUnresolved[] | string[] | undefined): PlanUnresolved[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((item, idx) => {
    if (typeof item === 'string') {
      return {requirement_id: `Unresolved ${idx + 1}`, reason: item};
    }
    const o = item as PlanUnresolved;
    return {
      requirement_id: o.requirement_id?.trim() ? o.requirement_id : `Unresolved ${idx + 1}`,
      reason: o.reason,
      status: o.status,
    };
  });
}

// Enhanced parsing function for unresolved requirements display
const parseReasonText = (text: string): ParsedReasonSegment[] => {
  if (!text || text.trim() === '') {
    return [{text: text || '', isQuestion: false}];
  }

  try {
    const trimmedText = text.trim();

    // Define parsing patterns with improved regex
    const questionPattern = /([^.!?]*\?)/g;
    const numberedPattern = /(\d+\.\s+[^.!?]*(?:[.!?]|$))/g;
    const parentheticalPattern = /(\(\d+\)[^(),]*(?:\([^)]*\)[^(),]*)*(?:,|$))/g;
    const bulletPattern = /([•\-*]\s+[^.!?]*(?:[.!?]|$))/gm;

    // Try to find matches using different patterns
    const questionMatches = Array.from(trimmedText.matchAll(questionPattern));
    const numberedMatches = Array.from(trimmedText.matchAll(numberedPattern));
    const parentheticalMatches = Array.from(trimmedText.matchAll(parentheticalPattern));
    const bulletMatches = Array.from(trimmedText.matchAll(bulletPattern));

    // Determine which parsing strategy to use based on matches found
    let segments: ParsedReasonSegment[] = [];

    // Strategy 1: Parse questions if found
    if (questionMatches.length > 1) {
      let lastIndex = 0;

      for (const match of questionMatches) {
        if (match.index !== undefined) {
          // Add any text before this question as a regular segment
          if (match.index > lastIndex) {
            const beforeText = trimmedText.slice(lastIndex, match.index).trim();
            if (beforeText) {
              segments.push({
                text: beforeText,
                isQuestion: false
              });
            }
          }

          // Add the question segment
          segments.push({
            text: match[0].trim(),
            isQuestion: true
          });

          lastIndex = match.index + match[0].length;
        }
      }

      // Add any remaining text
      if (lastIndex < trimmedText.length) {
        const remainingText = trimmedText.slice(lastIndex).trim();
        if (remainingText) {
          segments.push({
            text: remainingText,
            isQuestion: remainingText.includes('?')
          });
        }
      }
    }
    // Strategy 2: Parse numbered items if questions didn't work but numbered items found
    else if (numberedMatches.length > 1) {
      for (const match of numberedMatches) {
        segments.push({
          text: match[0].trim(),
          isQuestion: match[0].includes('?')
        });
      }
    }
    // Strategy 2b: Parse parenthetical numbered items if found
    else if (parentheticalMatches.length > 1) {
      let lastIndex = 0;

      // Add any text before the first parenthetical item
      if (parentheticalMatches[0].index !== undefined && parentheticalMatches[0].index > 0) {
        const beforeText = trimmedText.slice(0, parentheticalMatches[0].index).trim();
        if (beforeText) {
          segments.push({
            text: beforeText,
            isQuestion: beforeText.includes('?')
          });
        }
      }

      // Add each parenthetical item
      for (const match of parentheticalMatches) {
        segments.push({
          text: match[0].replace(/,$/, '').trim(), // Remove trailing comma
          isQuestion: match[0].includes('?')
        });

        if (match.index !== undefined) {
          lastIndex = match.index + match[0].length;
        }
      }

      // Add any remaining text after the last parenthetical item
      if (lastIndex < trimmedText.length) {
        const remainingText = trimmedText.slice(lastIndex).trim();
        if (remainingText) {
          segments.push({
            text: remainingText,
            isQuestion: remainingText.includes('?')
          });
        }
      }
    }
    // Strategy 3: Parse bullet points if found
    else if (bulletMatches.length > 1) {
      for (const match of bulletMatches) {
        segments.push({
          text: match[0].trim(),
          isQuestion: match[0].includes('?')
        });
      }
    }

    // Strategy 4: Single question detection
    if (segments.length === 0 && trimmedText.includes('?')) {
      segments.push({
        text: trimmedText,
        isQuestion: true
      });
    }

    // Fallback: return original text as single segment
    if (segments.length === 0) {
      segments.push({
        text: trimmedText,
        isQuestion: false
      });
    }

    // Clean up segments - remove empty ones and trim whitespace
    return segments
      .filter(segment => segment.text.trim().length > 0)
      .map(segment => ({
        ...segment,
        text: segment.text.trim()
      }));

  } catch (error) {
    // Error handling: return original text as fallback
    console.warn('Text parsing failed, falling back to original text:', error);
    return [{
      text: text,
      isQuestion: text.includes('?')
    }];
  }
};

// ============================================
// UNRESOLVED SECTION (separate, top, red)
// ============================================

function UnresolvedSection({items, onEditRequirement}: {items: PlanUnresolved[]; onEditRequirement?: (requirementId: string) => void}) {
  if (items.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3,
        borderColor: 'error.light',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'error.light',
        }}
      >
        <ErrorIcon sx={{color: 'error.dark', fontSize: 20}} />
        <Typography variant="subtitle2" sx={{color: 'error.dark', fontWeight: 600}}>
          Unresolved Requirements
        </Typography>
      </Box>
      {/* Items */}
      {items.map((item, idx) => (
        <UnresolvedItem key={idx} item={item} onEditRequirement={onEditRequirement} />
      ))}
    </Paper>
  );
}

function UnresolvedItem({item, onEditRequirement}: {item: PlanUnresolved; onEditRequirement?: (requirementId: string) => void}) {
  const [expanded, setExpanded] = useState(false);

  // Parse the reason text for better display using useMemo for performance
  const parsedSegments = useMemo(() => {
    try {
      return parseReasonText(item.reason);
    } catch (error) {
      console.warn('Parsing failed, falling back to original text:', error);
      return [{text: item.reason, isQuestion: item.reason.includes('?')}];
    }
  }, [item.reason]);

  // Generate preview text for collapsed state
  const previewText = useMemo(() => {
    if (!parsedSegments || parsedSegments.length === 0) {
      return item.reason;
    }

    if (parsedSegments.length === 1) {
      return parsedSegments[0].text;
    }

    // Show first segment + count indicator for multiple segments
    const firstSegment = parsedSegments[0].text;
    const additionalCount = parsedSegments.length - 1;

    // Truncate if the first segment is too long
    const maxPreviewLength = 100;
    const truncatedFirst = firstSegment.length > maxPreviewLength
      ? firstSegment.slice(0, maxPreviewLength) + '...'
      : firstSegment;

    return `${truncatedFirst} ... +${additionalCount} more`;
  }, [parsedSegments, item.reason]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row expansion
    if (item.requirement_id && onEditRequirement) {
      onEditRequirement(item.requirement_id);
    }
  };

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'error.light',
        cursor: 'pointer',
        '&:hover': {bgcolor: 'error.50'},
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <Box sx={{px: 1.5, py: 1.5, display: 'flex', alignItems: expanded ? 'flex-start' : 'center', gap: 1}}>
        {expanded ? (
          <ExpandLessIcon sx={{color: 'error.dark', fontSize: 20, mt: 0.25}} />
        ) : (
          <ExpandMoreIcon sx={{color: 'error.dark', fontSize: 20}} />
        )}
        {/* Requirement ID bubble */}
        <Chip
          label={item.requirement_id}
          size="small"
          sx={{
            bgcolor: 'error.50',
            color: 'error.dark',
            fontWeight: 600,
            fontSize: '0.7rem',
            borderColor: 'error.light',
            border: '1px solid',
            flexShrink: 0,
          }}
        />
        {/* Status badge */}
        {item.status && (
          <Chip
            icon={<HelpOutlineIcon sx={{fontSize: '14px !important'}} />}
            label={item.status.toUpperCase()}
            size="small"
            sx={{
              bgcolor: 'warning.50',
              color: 'warning.dark',
              fontWeight: 600,
              fontSize: '0.65rem',
              borderColor: 'warning.light',
              border: '1px solid',
              flexShrink: 0,
            }}
          />
        )}
        {/* Reason text - enhanced with parsing */}
        <Box sx={{flex: 1, minWidth: 0}}>
          {expanded ? (
            // Expanded state - show all parsed segments
            <Box>
              {parsedSegments && parsedSegments.length > 0 ? (
                parsedSegments.map((segment, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      mb: index < parsedSegments.length - 1 ? 1 : 0,
                      pl: index > 0 ? 1 : 0,
                      borderLeft: index > 0 ? '2px solid' : 'none',
                      borderLeftColor: 'error.light',
                      ...(segment.isQuestion && {
                        fontWeight: 'medium',
                      }),
                    }}
                  >
                    {segment.text}
                  </Typography>
                ))
              ) : (
                // Fallback to original text if segments are invalid
                <Typography variant="body2">
                  {item.reason}
                </Typography>
              )}
            </Box>
          ) : (
            // Collapsed state - show preview text
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {previewText}
            </Typography>
          )}
        </Box>
        {/* Edit Requirement button */}
        {item.requirement_id && onEditRequirement && (
          <Tooltip title="Edit this requirement to resolve the issue">
            <IconButton
              size="small"
              onClick={handleEditClick}
              sx={{
                ml: 1,
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50',
                },
                flexShrink: 0,
              }}
              aria-label={`Edit requirement ${item.requirement_id}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ============================================
// NOTES SECTION (separate, bottom, orange, collapsed by default)
// ============================================

function NotesSection({items}: {items: PlanNote[]}) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 3,
        borderColor: 'warning.light',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header (clickable to expand/collapse) */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': {bgcolor: 'warning.50'},
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ExpandLessIcon sx={{color: 'warning.dark', fontSize: 20}} />
        ) : (
          <ExpandMoreIcon sx={{color: 'warning.dark', fontSize: 20}} />
        )}
        <InfoIcon sx={{color: 'warning.dark', fontSize: 20}} />
        <Typography variant="subtitle2" sx={{color: 'warning.dark', fontWeight: 600}}>
          Notes
        </Typography>
        <Chip
          label={items.length}
          size="small"
          sx={{
            bgcolor: 'warning.50',
            color: 'warning.dark',
            fontWeight: 600,
            fontSize: '0.7rem',
            borderColor: 'warning.light',
            border: '1px solid',
            minWidth: 24,
            height: 22,
          }}
        />
      </Box>
      {/* Expandable note items */}
      <Collapse in={expanded}>
        {items.map((note, idx) => (
          <NoteItem key={idx} item={note} />
        ))}
      </Collapse>
    </Paper>
  );
}

function NoteItem({item}: {item: PlanNote}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'warning.light',
        cursor: 'pointer',
        '&:hover': {bgcolor: 'warning.50'},
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <Box sx={{px: 1.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1}}>
        {expanded ? (
          <ExpandLessIcon sx={{color: 'warning.dark', fontSize: 20}} />
        ) : (
          <ExpandMoreIcon sx={{color: 'warning.dark', fontSize: 20}} />
        )}
        {/* Requirement ID bubble */}
        <Chip
          label={item.requirement_id}
          size="small"
          sx={{
            bgcolor: 'warning.50',
            color: 'warning.dark',
            fontWeight: 600,
            fontSize: '0.7rem',
            borderColor: 'warning.light',
            border: '1px solid',
          }}
        />
        {/* Note text (truncated unless expanded) */}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: expanded ? 'visible' : 'hidden',
            textOverflow: expanded ? 'unset' : 'ellipsis',
            whiteSpace: expanded ? 'normal' : 'nowrap',
          }}
        >
          {item.text}
        </Typography>
      </Box>
    </Box>
  );
}

// ============================================
// ACTION ROW (redesigned for new plan format)
// ============================================

function ActionRow({
  action,
  actionIndex,
  executionOrder,
  onEdit,
  onDelete,
  disabled,
  requirementIdsWithNotes,
}: {
  action: PlanAction;
  actionIndex: number;
  executionOrder?: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  disabled: boolean;
  requirementIdsWithNotes: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const [xmlExpanded, setXmlExpanded] = useState(false);
  const newFmt = isNewFormat(action);

  const getActionIcon = () => {
    const actionType = action.type || action.action;
    if (actionType === 'add' || actionType.startsWith('add_') || actionType === 'create_custom_form') {
      return <AddIcon sx={{color: 'success.main', fontSize: 20}} />;
    }
    if (actionType === 'modify' || actionType.startsWith('modify_')) {
      return <BuildIcon sx={{color: 'info.main', fontSize: 20}} />;
    }
    if (actionType === 'remove' || actionType === 'remove_element' || actionType === 'delete_element') {
      return <DeleteIcon sx={{color: 'error.main', fontSize: 20}} />;
    }
    if (actionType === 'download_gl') {
      return <DownloadIcon sx={{color: 'primary.main', fontSize: 20}} />;
    }
    return <BuildIcon sx={{color: 'text.secondary', fontSize: 20}} />;
  };

  const getActionColor = (): 'error' | 'success' | 'info' | 'primary' | 'warning' => {
    const actionType = action.type || action.action;
    if (actionType === 'add' || actionType.startsWith('add_') || actionType === 'create_custom_form') return 'success';
    if (actionType === 'modify' || actionType.startsWith('modify_')) return 'info';
    if (actionType === 'remove' || actionType === 'remove_element' || actionType === 'delete_element') return 'error';
    if (actionType === 'download_gl') return 'primary';
    return 'info';
  };

  const title = getDescriptionTitle(action);
  const elementType = getElementType(action);
  const elementId = getElementId(action);
  const files = getAllTargetFiles(action);
  const steps = getDescriptionSteps(action);
  const concreteSteps = getConcreteSteps(action);
  const dependencies = getDependencies(action);
  const xmlByFileEntries = getXmlChangesByFile(action.xml_changes);
  const hasStructuredXmlBody = !!(
    action.xml_changes?.codelist_xml ||
    (action.xml_changes?.codelist_items && action.xml_changes.codelist_items.length > 0) ||
    (action.xml_changes?.proposed_changes && action.xml_changes.proposed_changes.length > 0) ||
    (action.xml_changes?.translation_changes && action.xml_changes.translation_changes.length > 0)
  );
  const hasXml = hasStructuredXmlBody || xmlByFileEntries.length > 0;

  return (
    <>
      <TableRow
        sx={{
          '& > *': {borderBottom: 'unset'},
          cursor: 'pointer',
          '&:hover': {bgcolor: 'action.hover'},
          '& .MuiTableCell-root': {py: 1.5},
        }}
        onClick={() => setOpen(!open)}
      >
        {/* Expand icon */}
        <TableCell sx={{width: 36, px: 1}}>
          <IconButton size="small">
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        {/* Execution order # */}
        <TableCell sx={{width: 36, px: 1, textAlign: 'center'}}>
          <Typography variant="body2" sx={{fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem'}}>
            {executionOrder != null ? executionOrder : actionIndex + 1}
          </Typography>
        </TableCell>
        {/* Action type + icon */}
        <TableCell sx={{width: 130}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            {getActionIcon()}
            <Chip
              label={formatActionType(action)}
              color={getActionColor()}
              size="small"
              sx={{fontSize: '0.7rem', height: 24, fontWeight: 500}}
            />
          </Box>
        </TableCell>
        {/* Description title */}
        <TableCell>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap={!open}
              sx={{
                maxWidth: open ? '100%' : 360,
                whiteSpace: open ? 'normal' : 'nowrap',
                overflow: open ? 'visible' : 'hidden',
                textOverflow: open ? 'unset' : 'ellipsis',
                wordBreak: open ? 'break-word' : 'normal',
              }}
            >
              {title}
            </Typography>
            {action.requirement_id && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
                <Chip
                  label={action.requirement_id}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{fontSize: '0.65rem', height: 20, fontWeight: 600}}
                />
                {requirementIdsWithNotes.has(action.requirement_id) && (
                  <InfoIcon sx={{color: 'warning.dark', fontSize: 16}} titleAccess="Has notes" />
                )}
              </Box>
            )}
          </Box>
        </TableCell>
        {/* Target (Element ID + Type) */}
        <TableCell>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            {elementType !== '-' && (
              <Chip
                label={elementType}
                size="small"
                variant="outlined"
                sx={{fontSize: '0.65rem', height: 22, fontWeight: 500, borderColor: 'grey.400'}}
              />
            )}
            <Typography variant="body2" fontWeight="medium" sx={{fontFamily: 'monospace', fontSize: '0.8rem'}} noWrap>
              {elementId !== '-' ? elementId : ''}
            </Typography>
          </Box>
        </TableCell>
        {/* Files */}
        <TableCell>
          <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 220}}>
            {files.slice(0, 3).map((file, idx) => (
              <Chip
                key={idx}
                icon={<FileIcon sx={{fontSize: '14px !important'}} />}
                label={getFileDisplay(file)}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                  '& .MuiChip-icon': {ml: 0.5},
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                }}
              />
            ))}
            {files.length > 3 && (
              <Chip
                label={`+${files.length - 3}`}
                size="small"
                sx={{fontSize: '0.65rem', height: 22, bgcolor: 'action.hover'}}
              />
            )}
          </Box>
        </TableCell>
        {/* Steps count badge */}
        <TableCell sx={{width: 70, textAlign: 'center'}}>
          {(steps.length > 0 || concreteSteps.length > 0) ? (
            <Tooltip title={`${steps.length} steps, ${concreteSteps.length} concrete`}>
              <Chip
                icon={<StepIcon sx={{fontSize: '14px !important'}} />}
                label={steps.length + concreteSteps.length}
                size="small"
                color="primary"
                variant="outlined"
                sx={{fontSize: '0.7rem', height: 22}}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.disabled">-</Typography>
          )}
        </TableCell>
        {/* Action buttons */}
        <TableCell align="center" sx={{width: 80}}>
          <Box sx={{display: 'flex', justifyContent: 'center'}} onClick={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              onClick={() => onEdit(actionIndex)}
              disabled={disabled}
              sx={{color: disabled ? 'action.disabled' : 'primary.main'}}
            >
              <EditIcon sx={{fontSize: 16}} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(actionIndex)}
              disabled={disabled}
              sx={{color: disabled ? 'action.disabled' : 'error.main'}}
            >
              <DeleteIcon sx={{fontSize: 16}} />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* ============ EXPANDED DETAILS ============ */}
      <TableRow>
        <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{mx: 2, my: 2.5}}>

              {/* -- Modification Steps -- */}
              {steps.length > 0 && (
                <Box sx={{mb: 2.5}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
                    <StepIcon sx={{fontSize: 18, color: 'primary.main'}} />
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      Modification Steps
                    </Typography>
                  </Box>
                  <Box sx={{pl: 1}}>
                    {steps.map((step, idx) => (
                      <Box key={idx} sx={{display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start'}}>
                        <Typography
                          variant="caption"
                          sx={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        >
                          {idx + 1}
                        </Typography>
                        <Typography variant="body2" sx={{fontSize: '0.8125rem', lineHeight: 1.5}}>
                          {step}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* -- Concrete Steps -- */}
              {concreteSteps.length > 0 && (
                <Box sx={{mb: 2.5}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
                    <CodeIcon sx={{fontSize: 18, color: 'success.main'}} />
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      Concrete Steps
                    </Typography>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: 'success.50',
                      borderColor: 'success.200',
                      borderRadius: 1.5,
                    }}
                  >
                    {concreteSteps.map((step, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          mb: idx < concreteSteps.length - 1 ? 0.5 : 0,
                        }}
                      >
                        {step}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}

              {/* -- Instructions (KB / v2 actions: always show when present) -- */}
              {newFmt && action.instructions && (
                <Box sx={{mb: 2.5}}>
                  <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                    Instructions
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{p: 2, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5}}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit',
                        fontSize: '0.8125rem',
                        m: 0,
                      }}
                    >
                      {action.instructions}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* -- Parameters (full object from API) -- */}
              {action.parameters && Object.keys(action.parameters).length > 0 && (
                <Box sx={{mb: 2.5}}>
                  <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                    Parameters
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{p: 1.5, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5, maxHeight: 280, overflow: 'auto'}}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        m: 0,
                        color: 'text.primary',
                      }}
                    >
                      {(() => {
                        try {
                          return JSON.stringify(action.parameters, null, 2);
                        } catch {
                          return String(action.parameters);
                        }
                      })()}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* -- Validation rules -- */}
              {Array.isArray(action.validation_rules) && action.validation_rules.length > 0 && (
                <Box sx={{mb: 2.5}}>
                  <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                    Validation rules
                  </Typography>
                  <Box component="ul" sx={{m: 0, pl: 2.5}}>
                    {action.validation_rules.map((rule, idx) => (
                      <Typography key={idx} component="li" variant="body2" sx={{fontSize: '0.8125rem', mb: 0.5}}>
                        {typeof rule === 'string' ? rule : JSON.stringify(rule)}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* -- Files Affected -- */}
              {files.length > 0 && (
                <Box sx={{mb: 2.5}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
                    <FileIcon sx={{fontSize: 18, color: 'text.secondary'}} />
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      Files Affected
                    </Typography>
                  </Box>
                  <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    {files.map((file, idx) => (
                      <Chip
                        key={idx}
                        icon={<FileIcon sx={{fontSize: '14px !important'}} />}
                        label={file}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                          '& .MuiChip-icon': {ml: 0.5},
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* -- Dependencies -- */}
              {dependencies.length > 0 && (
                <Box sx={{mb: 2.5}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
                    <DependencyIcon sx={{fontSize: 18, color: 'warning.main'}} />
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      Dependencies
                    </Typography>
                  </Box>
                  <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
                    {dependencies.map((dep, idx) => (
                      <Chip
                        key={idx}
                        label={dep}
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{fontSize: '0.75rem'}}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* -- XML Changes Preview -- */}
              {hasXml && (
                <Box sx={{mb: 2.5}}>
                  <Box
                    sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, cursor: 'pointer'}}
                    onClick={(e) => { e.stopPropagation(); setXmlExpanded(!xmlExpanded); }}
                  >
                    <XmlIcon sx={{fontSize: 18, color: 'secondary.main'}} />
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      XML Changes
                    </Typography>
                    {action.xml_changes?.codelist_item_count != null && (
                      <Chip
                        label={`${action.xml_changes.codelist_item_count} items`}
                        size="small"
                        sx={{fontSize: '0.65rem', height: 20, bgcolor: 'action.hover'}}
                      />
                    )}
                    <IconButton size="small" sx={{ml: 'auto'}}>
                      {xmlExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                  <Collapse in={xmlExpanded}>
                    {/* Codelist items summary */}
                    {action.xml_changes?.codelist_items && action.xml_changes.codelist_items.length > 0 && (
                      <Box sx={{mb: 1.5}}>
                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600, mb: 0.5, display: 'block'}}>
                          {action.xml_changes.codelist_name || 'Codelist'} ({action.xml_changes.codelist_items.length} items)
                          {action.xml_changes.next_coded_value && ` — Next value: ${action.xml_changes.next_coded_value}`}
                        </Typography>
                        <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem'}}>
                          <Box component="thead">
                            <Box component="tr" sx={{bgcolor: 'action.hover'}}>
                              <Box component="th" sx={{px: 1, py: 0.5, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider'}}>Value</Box>
                              <Box component="th" sx={{px: 1, py: 0.5, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider'}}>Text ID</Box>
                              <Box component="th" sx={{px: 1, py: 0.5, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider'}}>Display Name</Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {action.xml_changes.codelist_items.map((item, idx) => (
                              <Box component="tr" key={idx} sx={{'&:hover': {bgcolor: 'action.hover'}}}>
                                <Box component="td" sx={{px: 1, py: 0.25, fontFamily: 'monospace', borderBottom: '1px solid', borderColor: 'divider'}}>{item.coded_value}</Box>
                                <Box component="td" sx={{px: 1, py: 0.25, fontFamily: 'monospace', borderBottom: '1px solid', borderColor: 'divider'}}>{item.text_id}</Box>
                                <Box component="td" sx={{px: 1, py: 0.25, borderBottom: '1px solid', borderColor: 'divider'}}>{item.text_value}</Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    {/* Raw XML */}
                    {action.xml_changes?.codelist_xml && (
                      <Paper
                        variant="outlined"
                        sx={{p: 1.5, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5, maxHeight: 300, overflow: 'auto'}}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            m: 0,
                            color: 'text.primary',
                          }}
                        >
                          {action.xml_changes.codelist_xml}
                        </Typography>
                      </Paper>
                    )}

                    {/* Proposed Changes */}
                    {action.xml_changes?.proposed_changes && action.xml_changes.proposed_changes.length > 0 && (
                      <Box sx={{mt: 1.5}}>
                        <Typography variant="caption" sx={{fontWeight: 700, color: 'success.dark', mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5}}>
                          Proposed Changes
                        </Typography>
                        {action.xml_changes.proposed_changes.map((change: ProposedChange, idx: number) => (
                          <Box key={idx} sx={{mb: 1}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5}}>
                              <Chip
                                label={change.change_type.replace(/_/g, ' ')}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{fontSize: '0.65rem', height: 18, textTransform: 'uppercase'}}
                              />
                              {change.target_file && (
                                <Typography variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary'}}>
                                  {change.target_file}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="body2" sx={{fontSize: '0.8rem', mb: 0.5}}>
                              {change.description}
                            </Typography>
                            {change.xml_fragment && (
                              <Paper
                                variant="outlined"
                                sx={{p: 1.5, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5, maxHeight: 300, overflow: 'auto'}}
                              >
                                <Typography
                                  variant="body2"
                                  component="pre"
                                  sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.75rem', m: 0, color: 'text.primary'}}
                                >
                                  {change.xml_fragment}
                                </Typography>
                              </Paper>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Translation File Changes */}
                    {action.xml_changes?.translation_changes && action.xml_changes.translation_changes.length > 0 && (
                      <Box sx={{mt: 1.5}}>
                        <Typography variant="caption" sx={{fontWeight: 700, color: 'info.dark', mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5}}>
                          Translation File Changes
                        </Typography>
                        {action.xml_changes.translation_changes.map((change: TranslationChange, idx: number) => (
                          <Box key={idx} sx={{mb: 1}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5}}>
                              <Chip
                                label={change.change_type.replace(/_/g, ' ')}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{fontSize: '0.65rem', height: 18, textTransform: 'uppercase'}}
                              />
                              <Typography variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary'}}>
                                {change.target_file}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{fontSize: '0.8rem', mb: 0.5}}>
                              {change.description}
                            </Typography>
                            {change.xml_fragment && (
                              <Paper
                                variant="outlined"
                                sx={{p: 1.5, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5, maxHeight: 300, overflow: 'auto'}}
                              >
                                <Typography
                                  variant="body2"
                                  component="pre"
                                  sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.75rem', m: 0, color: 'text.primary'}}
                                >
                                  {change.xml_fragment}
                                </Typography>
                              </Paper>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Per-file XML snippets (filename → string maps from KB / v2 API) */}
                    {xmlByFileEntries.length > 0 && (
                      <Box sx={{mt: 1.5}}>
                        <Typography variant="caption" sx={{fontWeight: 700, color: 'secondary.dark', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5}}>
                          By target file
                        </Typography>
                        {xmlByFileEntries.map(({file, content}) => (
                          <Box key={file} sx={{mb: 1.5}}>
                            <Typography variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5}}>
                              {file}
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{p: 1.5, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5, maxHeight: 360, overflow: 'auto'}}
                            >
                              <Typography
                                variant="body2"
                                component="pre"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  m: 0,
                                  color: 'text.primary',
                                }}
                              >
                                {content}
                              </Typography>
                            </Paper>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Collapse>
                </Box>
              )}

              {/* -- Legacy: Full Instructions (for old format) -- */}
              {!newFmt && action.instructions && (
                <Box sx={{mb: 2}}>
                  <Typography variant="subtitle2" gutterBottom>
                    Instructions:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{p: 2, bgcolor: 'action.hover', borderColor: 'divider', borderRadius: 1.5}}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.8125rem',
                        m: 0,
                      }}
                    >
                      {action.instructions}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* -- Confidence -- */}
              {action.confidence && action.confidence_reason && (
                <Box sx={{mb: 2}}>
                  <Typography variant="subtitle2" gutterBottom>
                    Confidence:
                  </Typography>
                  <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-start'}}>
                    <Chip
                      label={action.confidence.toUpperCase()}
                      size="small"
                      color={
                        action.confidence === 'high' ? 'success'
                          : action.confidence === 'medium' ? 'warning'
                            : 'error'
                      }
                      sx={{fontWeight: 'bold', minWidth: 70}}
                    />
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        flex: 1,
                        bgcolor: action.confidence === 'high' ? 'success.50'
                          : action.confidence === 'medium' ? 'warning.50'
                            : 'error.50',
                        borderColor: action.confidence === 'high' ? 'success.200'
                          : action.confidence === 'medium' ? 'warning.200'
                            : 'error.200',
                      }}
                    >
                      <Typography variant="body2">{action.confidence_reason}</Typography>
                    </Paper>
                  </Box>
                </Box>
              )}

              {/* -- Also fulfills -- */}
              {action.also_fulfills && action.also_fulfills.length > 0 && (
                <Box sx={{mb: 2}}>
                  <Typography variant="subtitle2" gutterBottom>
                    Also Fulfills Requirements:
                  </Typography>
                  <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    {action.also_fulfills.map((reqId, idx) => (
                      <Chip
                        key={idx}
                        label={reqId}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* -- Metadata footer -- */}
              <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1, borderTop: '1px solid', borderColor: 'divider'}}>
                {action.id && (
                  <Typography variant="caption" color="text.disabled">
                    ID: {action.id}
                  </Typography>
                )}
                {action.parameters?.kb_entry_type && (
                  <Typography variant="caption" color="text.disabled">
                    Pattern: {action.parameters.kb_entry_type}
                  </Typography>
                )}
                {action.parameters?.kb_platform && (
                  <Typography variant="caption" color="text.disabled">
                    Platform: {action.parameters.kb_platform}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled" component="span" sx={{display: 'block'}}>
                  {action.target_section && (
                    <>
                      Section: {action.target_section}
                      {' · '}
                    </>
                  )}
                  Target file: {action.target_file || '—'} | Type: {elementType} | Element: {elementId}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export const PlanActionsTable: React.FC<PlanActionsTableProps> = ({
  plan,
  cached,
  generatedAt,
  onPlanChanged,
  onEditRequirementRequested,
}) => {
  const { setTargetRequirement } = useWizardNavigation();
  // Local state for actions (allows editing before save)
  const [localActions, setLocalActions] = useState<PlanAction[] | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingActionIndex, setEditingActionIndex] = useState<number | null>(null);
  const [editedInstructions, setEditedInstructions] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingActionIndex, setDeletingActionIndex] = useState<number | null>(null);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false,
    message: '',
    severity: 'success',
  });

  const notes = useMemo(() => normalizePlanNotes(plan?.notes), [plan?.notes]);
  const unresolved = useMemo(() => normalizePlanUnresolved(plan?.unresolved), [plan?.unresolved]);

  // Requirement IDs that have notes (for showing info icon in the actions table)
  const requirementIdsWithNotes = useMemo(
    () => new Set(notes.map(n => n.requirement_id)),
    [notes]
  );

  // Get session info from plan
  const trialId = plan?.trial_id;
  const sessionId = plan?.session_id;
  const platform = plan?.platform || 'app';

  // Merge actions from surgical_plan (richer) if available, falling back to plan.actions
  const mergedActions = useMemo(() => {
    const base = localActions ?? (plan?.actions || []);
    const surgical = plan?.surgical_plan?.actions;
    if (!surgical || surgical.length === 0) return base;

    const surgicalById = new Map(surgical.filter(a => a.id).map(a => [a.id, a]));
    return base.map(action => {
      if (action.id && surgicalById.has(action.id)) {
        return {...surgicalById.get(action.id)!, ...action, ...surgicalById.get(action.id)!};
      }
      return action;
    });
  }, [localActions, plan?.actions, plan?.surgical_plan?.actions]);

  // Build execution order map: action_id -> order number
  const executionOrderMap = useMemo(() => {
    const order = plan?.surgical_plan?.execution_order;
    if (!order) return new Map<string, number>();
    return new Map(order.map((id, idx) => [id, idx + 1]));
  }, [plan?.surgical_plan?.execution_order]);

  // Sort actions: respect execution_order when available, otherwise priority-based
  const sortedActions = useMemo(() => {
    if (!mergedActions.length) return [];
    return [...mergedActions].sort((a, b) => {
      const getPriority = (action: PlanAction) => {
        if (action.action === 'unresolved') return 0;
        if (action.action === 'note') return 2;
        return 1;
      };
      const priA = getPriority(a);
      const priB = getPriority(b);
      if (priA !== priB) return priA - priB;
      if (a.id && b.id && executionOrderMap.size > 0) {
        const oA = executionOrderMap.get(a.id) ?? 999;
        const oB = executionOrderMap.get(b.id) ?? 999;
        return oA - oB;
      }
      return 0;
    });
  }, [mergedActions, executionOrderMap]);

  const executableActions = sortedActions.filter(
    a => a.action !== 'unresolved' && a.action !== 'note'
  );

  // Reset local state when source data changes from parent
  useEffect(() => {
    setLocalActions(null);
  }, [plan]);

  // Handler for Edit Requirement button
  const handleEditRequirement = useCallback((requirementId: string) => {
    if (!requirementId) {
      console.warn('Cannot edit requirement: missing requirement_id');
      return;
    }

    // Set navigation target in context
    setTargetRequirement(requirementId, 'unresolved_action', {
      highlight: true,
      showMessage: true,
      autoScroll: true,
    });

    // Optional callback to parent (for additional coordination)
    onEditRequirementRequested?.(requirementId);
  }, [setTargetRequirement, onEditRequirementRequested]);

  // Save actions to backend
  const saveActions = useCallback(async (actions: PlanAction[]): Promise<boolean> => {
    if (!trialId || !sessionId) {
      console.error('Missing trialId or sessionId for saving');
      setSnackbar({open: true, message: 'Cannot save: missing session info', severity: 'error'});
      return false;
    }

    setSaving(true);
    console.log('💾 Saving plan actions:', actions);

    try {
      await apiService.saveSurgicalPlan(trialId, sessionId, platform, actions, true);
      console.log('✅ Plan saved successfully');

      // Update local state immediately for responsive UI
      setLocalActions([...actions]);

      setSnackbar({open: true, message: 'Plan saved successfully', severity: 'success'});

      // Notify parent
      onPlanChanged?.();
      return true;
    } catch (error) {
      console.error('Error saving plan:', error);
      setSnackbar({open: true, message: 'Error saving plan', severity: 'error'});
      return false;
    } finally {
      setSaving(false);
    }
  }, [trialId, sessionId, platform, onPlanChanged]);

  // Edit handlers
  const handleEdit = useCallback((index: number) => {
    setEditingActionIndex(index);
    setEditedInstructions(sortedActions[index]?.instructions || '');
    setEditDialogOpen(true);
  }, [sortedActions]);

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingActionIndex(null);
    setEditedInstructions('');
  };

  const handleSaveEdit = async () => {
    if (editingActionIndex === null) return;

    const updatedActions = sortedActions.map((action, idx) =>
      idx === editingActionIndex
        ? {...action, instructions: editedInstructions.trim()}
        : action
    );

    const success = await saveActions(updatedActions);
    if (success) {
      handleCloseEditDialog();
    }
  };

  // Delete handlers
  const handleDelete = useCallback((index: number) => {
    setDeletingActionIndex(index);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingActionIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (deletingActionIndex === null) return;

    const updatedActions = sortedActions.filter((_, idx) => idx !== deletingActionIndex);

    const success = await saveActions(updatedActions);
    if (success) {
      handleCloseDeleteDialog();
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({...prev, open: false}));
  };

  if (!plan || (executableActions.length === 0 && unresolved.length === 0 && notes.length === 0)) {
    return (
      <Alert severity="warning">
        No actions found in the plan.
      </Alert>
    );
  }

  // Download raw JSON for debugging
  const handleDownloadJSON = () => {
    const jsonStr = JSON.stringify(plan, null, 2);
    const blob = new Blob([jsonStr], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plan_${plan.plan_id || 'debug'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totalSteps = executableActions.reduce((sum, a) => sum + getDescriptionSteps(a).length + getConcreteSteps(a).length, 0);

  return (
    <Box>
      {/* Plan overview header */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: 'action.hover',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', mb: plan.reasoning ? 1.5 : 0}}>
          <FolderIcon sx={{color: 'primary.main', fontSize: 24, mr: 1}} />
          <Typography variant="subtitle1" sx={{fontWeight: 600}}>
            Plan Overview
          </Typography>
          <Box sx={{ml: 'auto', display: 'flex', gap: 1, alignItems: 'center'}}>
            {plan.kb_confidence != null && (
              <Tooltip title={`Knowledge base confidence: ${Math.round(plan.kb_confidence * 100)}%`}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <LinearProgress
                    variant="determinate"
                    value={plan.kb_confidence * 100}
                    sx={{
                      width: 60,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: plan.kb_confidence >= 0.8 ? 'success.main' : plan.kb_confidence >= 0.5 ? 'warning.main' : 'error.main',
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                    {Math.round(plan.kb_confidence * 100)}%
                  </Typography>
                </Box>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{fontSize: 16}} />}
              onClick={handleDownloadJSON}
              sx={{fontSize: '0.75rem', textTransform: 'none'}}
            >
              Download JSON
            </Button>
          </Box>
        </Box>
        {plan.reasoning && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
            {plan.reasoning}
          </Typography>
        )}
        {/* Metadata chips row */}
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
          {plan.plan_id && (
            <Chip label={`Plan: ${plan.plan_id}`} size="small" variant="outlined" sx={{fontSize: '0.7rem', height: 22}} />
          )}
          {plan.platform && (
            <Chip label={plan.platform.toUpperCase()} size="small" color="primary" variant="outlined" sx={{fontSize: '0.7rem', height: 22, fontWeight: 600}} />
          )}
          {plan.feature_count != null && (
            <Chip label={`${plan.feature_count} features`} size="small" sx={{fontSize: '0.7rem', height: 22, bgcolor: 'primary.50', color: 'primary.main'}} />
          )}
          {plan.dependency_count != null && (
            <Chip label={`${plan.dependency_count} dependencies`} size="small" sx={{fontSize: '0.7rem', height: 22, bgcolor: 'warning.50', color: 'warning.dark'}} />
          )}
          {totalSteps > 0 && (
            <Chip icon={<StepIcon sx={{fontSize: '14px !important'}} />} label={`${totalSteps} total steps`} size="small" sx={{fontSize: '0.7rem', height: 22, bgcolor: 'success.50', color: 'success.dark'}} />
          )}
          {plan.generated_at && (
            <Typography variant="caption" color="text.disabled" sx={{ml: 'auto'}}>
              Generated: {new Date(plan.generated_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* 1. Unresolved section (top, separate, red) */}
      <UnresolvedSection items={unresolved} onEditRequirement={handleEditRequirement} />

      {/* 2. Actions table (middle) */}
      {executableActions.length > 0 && (
        <Paper variant="outlined" sx={{borderRadius: 2, overflow: 'hidden'}}>
          <TableContainer
            sx={{
              overflowX: 'auto',
              scrollbarGutter: 'stable',
              '&::-webkit-scrollbar': { height: 12 },
              '&::-webkit-scrollbar-track': { bgcolor: 'grey.200' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 6 },
              '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.500' },
            }}
          >
            <Table aria-label="plan actions table" sx={{minWidth: 900}}>
              <TableHead>
                <TableRow sx={{bgcolor: 'action.hover'}}>
                  <TableCell sx={{width: 36, px: 1}} />
                  <TableCell sx={{width: 36, px: 1, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5, textAlign: 'center'}}>#</TableCell>
                  <TableCell sx={{fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5, width: 130}}>Action</TableCell>
                  <TableCell sx={{fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>Description</TableCell>
                  <TableCell sx={{fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>Target</TableCell>
                  <TableCell sx={{fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>Files</TableCell>
                  <TableCell sx={{fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5, textAlign: 'center', width: 70}}>Steps</TableCell>
                  <TableCell sx={{fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5, textAlign: 'center'}}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executableActions.map((action, index) => (
                  <ActionRow
                    key={action.id || index}
                    action={action}
                    actionIndex={index}
                    executionOrder={action.id ? executionOrderMap.get(action.id) : undefined}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    disabled={saving}
                    requirementIdsWithNotes={requirementIdsWithNotes}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* 3. Notes section (bottom, separate, orange, collapsed by default) */}
      <NotesSection items={notes} />

      {/* Summary footer */}
      <Box sx={{mt: 2.5, display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center'}}>
        <Typography variant="caption" color="text.secondary">
          {executableActions.length} action{executableActions.length !== 1 ? 's' : ''}
        </Typography>
        {totalSteps > 0 && (
          <Typography variant="caption" color="primary.main" sx={{fontWeight: 500}}>
            {totalSteps} modification step{totalSteps !== 1 ? 's' : ''}
          </Typography>
        )}
        {unresolved.length > 0 && (
          <Typography variant="caption" color="error.main" sx={{fontWeight: 500}}>
            {unresolved.length} unresolved
          </Typography>
        )}
        <Typography variant="caption" color="success.main" sx={{fontWeight: 500}}>
          {executableActions.length} ready
        </Typography>
        {notes.length > 0 && (
          <Typography variant="caption" color="warning.main" sx={{fontWeight: 500}}>
            {notes.length} note{notes.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Typography variant="h6">
                Edit Action Instructions
              </Typography>
              {editingActionIndex !== null && sortedActions[editingActionIndex] && (
                <Chip
                  label={formatActionType(sortedActions[editingActionIndex])}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{fontWeight: 600}}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseEditDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            label="Instructions"
            multiline
            rows={8}
            fullWidth
            value={editedInstructions}
            onChange={(e) => setEditedInstructions(e.target.value)}
            variant="outlined"
            placeholder="Edit the action instructions..."
            disabled={saving}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Action?</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {deletingActionIndex !== null && sortedActions[deletingActionIndex] && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Are you sure you want to delete this action?
              </Typography>
              <Paper variant="outlined" sx={{p: 2, mt: 2, bgcolor: 'warning.light'}}>
                <Box sx={{mb: 1}}>
                  <Chip
                    label={formatActionType(sortedActions[deletingActionIndex])}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{fontWeight: 600, mr: 1}}
                  />
                  {sortedActions[deletingActionIndex].requirement_id && (
                    <Chip
                      label={sortedActions[deletingActionIndex].requirement_id}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{fontWeight: 600}}
                    />
                  )}
                </Box>
                {sortedActions[deletingActionIndex].instructions && (
                  <Typography variant="body2" sx={{mt: 1}}>
                    {sortedActions[deletingActionIndex].instructions}
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification for feedback */}
      <Notification
        snackbarProps={{
          open: snackbar.open,
          autoHideDuration: 4000,
          onClose: handleCloseSnackbar,
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
        }}
        alertProps={{
          onClose: handleCloseSnackbar,
          severity: snackbar.severity,
        }}
      >
        {snackbar.message}
      </Notification>
    </Box>
  );
};
